/**
 * Lexicon Analysis Service
 *
 * LLM-powered NLP meta-model analysis of meeting transcripts.
 * Scores each speaker on six communication dimensions and surfaces
 * deletion, distortion, and generalization patterns.
 */

import { callAgentLlm } from './llmClient';
import { isAiCapabilityEnabled } from './ai/aiAssistSettings';
import type {
  LlmAnalysisOutput,
  LlmSpeakerResult,
  DetectedPattern,
} from '../types/prioritization/lexicon';
import { DEFAULT_MODEL } from '../../supabase/functions/_shared/modelRequest.ts';

// ── Constants ─────────────────────────────────────────────────────────────────

const ANALYSIS_MODEL = DEFAULT_MODEL;
const MAX_TRANSCRIPT_CHARS = 50_000; // safety limit before chunking

// ── System prompt ─────────────────────────────────────────────────────────────

const SYSTEM_PROMPT = `You are an expert in Neuro-Linguistic Programming (NLP) meta-model analysis and communication pattern recognition. You analyze meeting transcripts to surface language patterns per speaker.

## Your task

Given a meeting transcript, analyze EACH speaker and return a structured JSON object.

## Scoring dimensions (0–100)

Score each speaker on:

1. **Specificity** (0=very vague/unspecified, 100=concrete and referenced)
   - Low: "We need to improve things", "They don't get it", "It's better this way"
   - High: "We need to reduce P1 ticket resolution time from 48h to 24h by Q3"

2. **Clarity** (0=high distortion, 100=clear factual language)
   - Low: mind reading, cause-effect chains, presuppositions, complex equivalences
   - High: factual, observable, distinguished from interpretation

3. **Flexibility** (0=rigid generalizations, 100=nuanced and open)
   - Low: "always/never", "must/have to/can't", "everyone/no one"
   - High: "in some cases", "my experience has been", "what would it take to..."

4. **Agency** (0=victim/reactive language, 100=ownership and internal locus)
   - Low: "They make it hard", "We can't do anything until...", "It just happens"
   - High: "I will...", "We're choosing to...", "I'm accountable for..."

5. **Momentum** (0=no action commitments, 100=high action density)
   - Measure: % of turns that contain a concrete commitment, action verb, or decision
   - Low: mostly analysis, discussion, questions without commits
   - High: "I'll have this by Friday", "Let's decide now", "I'm taking this"

6. **Strategic Index** (0=purely operational detail, 100=purely strategic/outcome-level)
   - Low: implementation specifics, line-level debugging, tactical logistics
   - High: outcomes, objectives, tradeoffs, long-term positioning, system-level framing

## NLP Meta-Model pattern detection

Identify instances of:

### Deletions
- **Simple deletion**: information is left out ("I was uncomfortable" — about what?)
- **Comparative deletion**: comparison without reference ("This is better" — than what?)
- **Lack of referential index**: unspecified nouns ("They don't listen" — who specifically?)
- **Unspecified verb**: vague process ("She rejected me" — how exactly?)
- **Nominalization**: turning a process into a thing ("the decision", "the alignment", "our relationship with quality")

### Distortions
- **Mind reading**: claiming to know another's thoughts ("They think we're moving too fast")
- **Lost performative**: value judgement without attribution ("It's wrong to ship without tests")
- **Cause and effect**: one thing forces another ("The latency is causing all our problems")
- **Complex equivalence**: A = B without evidence ("She's always late, she doesn't care")
- **Presupposition**: unstated assumption required to make sense ("When are you going to fix this?")

### Generalizations
- **Universal quantifier**: always, never, everyone, no one, every time
- **Modal operator (necessity)**: must, should, have to, need to, required to
- **Modal operator (possibility)**: can't, won't, impossible, it's not possible, there's no way

## Output format

Return ONLY valid JSON matching this exact structure:

{
  "speakers": [
    {
      "speakerName": "string",
      "scores": {
        "specificity": 0-100,
        "clarity": 0-100,
        "flexibility": 0-100,
        "agency": 0-100,
        "momentum": 0-100,
        "strategicIndex": 0-100
      },
      "actionCommitmentRate": 0-100,
      "questionRate": 0-100,
      "hedgingFrequency": 0-100,
      "topTopics": ["topic1", "topic2", ...],
      "keyPhrases": ["phrase1", ...],
      "notableQuotes": ["verbatim quote 1", ...],
      "detectedPatterns": [
        {
          "category": "deletion"|"distortion"|"generalization",
          "type": "pattern type name",
          "count": number,
          "examples": ["verbatim example", ...]
        }
      ],
      "strengths": ["strength 1", ...],
      "blindSpots": ["blind spot 1", ...],
      "recommendations": ["recommendation 1", ...],
      "wordCount": number,
      "turnCount": number
    }
  ]
}

Important:
- Only include speakers who have a meaningful amount of dialogue (at least 3 turns or 50 words)
- notableQuotes must be verbatim from the transcript
- detectedPatterns should only include patterns with count >= 2
- recommendations should be specific and actionable coaching suggestions
- Return ONLY the JSON object, no markdown fences, no preamble`;

// ── Transcript parser ─────────────────────────────────────────────────────────

/**
 * Detect speaker names from a transcript.
 * Supports common formats:
 *   "Jordan: ..."
 *   "[Jordan Mercer] ..."
 *   "Jordan Mercer (10:24): ..."
 *   "JORDAN: ..."
 */
export function detectSpeakers(rawContent: string): string[] {
  const patterns = [
    /^([A-Z][a-zA-Z\s\-']+):\s/gm,           // "Jordan: " or "Jordan Mercer: "
    /^\[([A-Z][a-zA-Z\s\-']+)\]/gm,           // "[Jordan Mercer]"
    /^([A-Z][a-zA-Z\s\-']+)\s*\(\d+:\d+\):/gm, // "Jordan (10:24):"
    /^([A-Z]{2,}(?:\s[A-Z]{2,})*):\s/gm,      // "JORDAN:" or "JORDAN MERCER:"
  ];

  const names = new Set<string>();
  for (const pattern of patterns) {
    let match;
    while ((match = pattern.exec(rawContent)) !== null) {
      const name = match[1].trim();
      // Normalise ALL-CAPS names
      const normalised = name === name.toUpperCase() && name.length > 2
        ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()
        : name;
      if (normalised.length > 1 && normalised.length < 50) {
        names.add(normalised);
      }
    }
  }

  return Array.from(names);
}

// ── Analysis ──────────────────────────────────────────────────────────────────

export interface AnalyzeTranscriptInput {
  transcriptId: string;
  rawContent: string;
  title: string;
  workspaceId?: string;
  conversationId?: string;
}

export interface AnalyzeTranscriptResult {
  data: LlmAnalysisOutput | null;
  error: string | null;
}

export async function analyzeTranscript(
  input: AnalyzeTranscriptInput
): Promise<AnalyzeTranscriptResult> {
  if (!isAiCapabilityEnabled('lexicon_analysis', input.workspaceId)) {
    return { data: null, error: 'Lexicon analysis is disabled for this workspace.' };
  }

  try {
    const content = input.rawContent.slice(0, MAX_TRANSCRIPT_CHARS);

    const userPrompt = `Meeting title: ${input.title}

TRANSCRIPT:
${content}

Analyze all speakers in this transcript and return the JSON structure as specified.`;

    const raw = await callAgentLlm({
      userPrompt,
      systemPrompt: SYSTEM_PROMPT,
      model: ANALYSIS_MODEL,
      conversationId: input.conversationId ?? `lexicon-${input.transcriptId}`,
      maxTokens: 4000,
      temperature: 0.1,
    });

    const parsed = parseAnalysisResponse(raw);
    if (!parsed) {
      return { data: null, error: 'Failed to parse LLM response as valid analysis JSON.' };
    }

    return { data: parsed, error: null };
  } catch (err) {
    return {
      data: null,
      error: err instanceof Error ? err.message : 'Unknown error during transcript analysis',
    };
  }
}

// ── Response parser ───────────────────────────────────────────────────────────

function parseAnalysisResponse(raw: string): LlmAnalysisOutput | null {
  try {
    // Strip markdown fences if present
    const cleaned = raw.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '').trim();
    const parsed = JSON.parse(cleaned);

    if (!parsed || !Array.isArray(parsed.speakers)) return null;

    return {
      speakers: parsed.speakers.map(normalizeSpeakerResult).filter(Boolean) as LlmSpeakerResult[],
    };
  } catch {
    // Try to extract JSON from within the response
    const jsonMatch = raw.match(/\{[\s\S]*"speakers"[\s\S]*\}/);
    if (!jsonMatch) return null;
    try {
      const inner = JSON.parse(jsonMatch[0]);
      if (!Array.isArray(inner.speakers)) return null;
      return {
        speakers: inner.speakers.map(normalizeSpeakerResult).filter(Boolean) as LlmSpeakerResult[],
      };
    } catch {
      return null;
    }
  }
}

function clamp(v: unknown, min = 0, max = 100): number {
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  if (isNaN(n)) return 50;
  return Math.min(max, Math.max(min, Math.round(n)));
}

function normalizeSpeakerResult(raw: unknown): LlmSpeakerResult | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;

  if (typeof r.speakerName !== 'string' || !r.speakerName.trim()) return null;
  if (!r.scores || typeof r.scores !== 'object') return null;

  const scores = r.scores as Record<string, unknown>;
  const patterns = Array.isArray(r.detectedPatterns)
    ? r.detectedPatterns.map(normalizePattern).filter(Boolean) as DetectedPattern[]
    : [];

  return {
    speakerName: r.speakerName.trim(),
    scores: {
      specificity:    clamp(scores.specificity),
      clarity:        clamp(scores.clarity),
      flexibility:    clamp(scores.flexibility),
      agency:         clamp(scores.agency),
      momentum:       clamp(scores.momentum),
      strategicIndex: clamp(scores.strategicIndex),
    },
    actionCommitmentRate: clamp(r.actionCommitmentRate),
    questionRate:         clamp(r.questionRate),
    hedgingFrequency:     clamp(r.hedgingFrequency),
    topTopics:    toStringArray(r.topTopics),
    keyPhrases:   toStringArray(r.keyPhrases),
    notableQuotes: toStringArray(r.notableQuotes),
    detectedPatterns: patterns,
    strengths:       toStringArray(r.strengths),
    blindSpots:      toStringArray(r.blindSpots),
    recommendations: toStringArray(r.recommendations),
    wordCount:  typeof r.wordCount === 'number' ? r.wordCount : 0,
    turnCount:  typeof r.turnCount === 'number' ? r.turnCount : 0,
  };
}

function normalizePattern(raw: unknown): DetectedPattern | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (!['deletion', 'distortion', 'generalization'].includes(String(r.category))) return null;
  if (typeof r.type !== 'string') return null;
  return {
    category: r.category as DetectedPattern['category'],
    type: r.type,
    count: typeof r.count === 'number' ? r.count : 1,
    examples: toStringArray(r.examples),
  };
}

function toStringArray(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.filter(x => typeof x === 'string').map(x => (x as string).trim()).filter(Boolean);
}
